import React, { useState, useRef, useEffect } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useMyContextFunctions } from '../../context/ContextFunctions';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
  profileSlug,
} from '../../util/data';
import { richText } from '../../util/richText';
import { resolveReturnPolicy } from '../../util/dataExtractors';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { getListingCategories } from '../../ducks/hostedAssets.duck';
import { getCategoryBreadcrumbs } from '../../util/categoryHelpers';
import { getCategoryUrl } from '../../util/categoryLandingHelpers';

import {
  H4,
  Page,
  Modal,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleMakeOffer,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import InquiryForm from './InquiryForm/InquiryForm';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionGallery from './SectionGallery';

import css from './ListingPage.module.css';
import { HANDLING_BUSINESS_DAYS } from '../../util/handlingTime';
import { withViewport } from '../../util/uiHelpers.js';
import IconNextArrow from '../../components/IconNextArrow/IconNextArrow.js';
import {
  getDeliveryMethod,
  getShippingAddressID,
} from '../../util/dataExtractors.js';
import { extractListingDataForCart } from '../CartPage/cartHelpers.js';
import SocialSharing from './SocialSharing/SocialSharing.js';
import { isArrayLength } from '../../util/genericHelpers.js';
import OfferModal from './OfferForm/OfferModal.js';
const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

// Payment methods section removed - sellers no longer required to set up payout methods before listing

export const ListingPageComponent = props => {
  // Desktop asks a guest to sign in in the modal, over this page
  const { onRequireAuth } = useMyContextFunctions();
  // Check for ?inquiry=true query param (from Contact Shop on profile page). Read from the
  // router's location, so the server render and the browser's first render agree.
  const inquiryFromQuery =
    new URLSearchParams(props.location?.search || '').get('inquiry') === 'true';
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id || inquiryFromQuery
  );
  const [isSocialSharingModalOpen, setIsSocialSharingModalOpen] = useState(false);
  const [isOffersModalOpen, setIsOffersModalOpen] = useState(false);
  const breadcrumbsRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const {
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    sendInquiryInProgress,
    sendInquiryError,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    listingCategories,
    isAuthenticated,
  } = props;

  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  // The breadcrumb trail fades at its end when it's wider than its row: measured once the
  // listing is on the page (and again for each listing, and on resize)
  const breadcrumbsKey = `${currentListing.id?.uuid}|${currentListing.attributes.title}`;
  useEffect(() => {
    const el = breadcrumbsRef.current;
    if (!el) return undefined;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [breadcrumbsKey]);

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    price = null,
    title = '',
    publicData = {},
  } = currentListing.attributes;

  // Three-tier resolution (listing → shop → platform); the author with
  // publicData.shopReturnPolicy is read off the denormalized listing.
  const returnPolicy = resolveReturnPolicy(currentListing);
  // How it reaches the buyer: shipped (from where the seller is) or collected
  const listingDeliveryMethods = currentListing.attributes.publicData?.deliveryMethods || [];
  const shipsToBuyer =
    listingDeliveryMethods.length === 0 || listingDeliveryMethods.includes('shipping');

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const sellerPublicData = ensuredAuthor.attributes?.profile?.publicData || {};
  const sellerPlace =
    sellerPublicData.displayLocation ||
    [sellerPublicData.location?.city, sellerPublicData.location?.state].filter(Boolean).join(', ') ||
    null;
  const from_address = getShippingAddressID(currentAuthor);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
    onRequireAuth,
  });

  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    from_address,
    cartItems: [extractListingDataForCart(currentListing)],
    onRequireAuth,
  });

  const handleOrderSubmit = async values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;

  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const onOpenOffersModal = handleMakeOffer({
    ...commonParams,
    currentUser,
    location,
    setOffersModalOpen: setIsOffersModalOpen,
    onRequireAuth,
  });

  // Get category breadcrumbs using hosted categories
  const categoryBreadcrumbs = getCategoryBreadcrumbs(listingCategories, publicData);

  const wrapperClasses = classNames(css.breadcrumbsWrapper, {
    [css.breadcrumbsOverflowing]: isOverflowing,
  });

  const breadcrumbs = (
    <div className={wrapperClasses}>
      <div className={css.breadcrumbs} ref={breadcrumbsRef}>
        <NamedLink className={css.link} name="LandingPage">
          <FormattedMessage id="ListingPage.breadcrumbHome" />
        </NamedLink>
        {categoryBreadcrumbs.map((category) => (
          <React.Fragment key={category.id}>
            <IconNextArrow />
            <Link
              className={css.link}
              to={getCategoryUrl(category.id, listingCategories) || '/'}
            >
              {category.label}
            </Link>
          </React.Fragment>
        ))}
        <IconNextArrow />
        <span className={css.current}>{title}</span>
      </div>
    </div>
  );
  const authorSlug = profileSlug(ensuredAuthor);
  const deliveryMethod = getDeliveryMethod(currentListing);
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          {breadcrumbs}
          <div className={css.mainContentWrapper}>
            <div className={css.galleryColumn}>
              {currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            </div>
            <div className={css.orderColumnForProductLayout}>
              <OrderPanel
                className={css.productOrderPanel}
                listing={currentListing}
                isOwnListing={isOwnListing}
                editListingParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
                onSubmit={handleOrderSubmit}
                onOpenOffersModal={onOpenOffersModal}
                deliveryMethod={deliveryMethod}
                authorLink={
                  authorSlug ? (
                    <NamedLink
                      className={css.authorNameLink}
                      name="ProfilePage"
                      params={{ username: authorSlug }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  ) : (
                    <span className={css.authorNameLink}>{authorDisplayName}</span>
                  )
                }
                title={
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                }
                titleDesktop={
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                }
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                setIsSocialSharingModalOpen={setIsSocialSharingModalOpen}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
              />
              {/* What a buyer wants to know before buying: when it ships,
                  returns, and TradProtect */}
              <ul className={css.facts}>
                <li className={css.fact}>
                  <svg className={css.factIcon} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" />
                    <circle cx="7" cy="17.5" r="1.8" />
                    <circle cx="17" cy="17.5" r="1.8" />
                  </svg>
                  <span className={css.factText}>
                    <strong>
                      {shipsToBuyer ? (
                        <FormattedMessage
                          id="OrderPanel.shipsWithin"
                          values={{ days: HANDLING_BUSINESS_DAYS }}
                        />
                      ) : (
                        <FormattedMessage id="OrderPanel.pickupOnly" />
                      )}
                    </strong>
                    {shipsToBuyer && sellerPlace ? (
                      <span>
                        <FormattedMessage id="ListingPage.shipsFrom" values={{ place: sellerPlace }} />
                      </span>
                    ) : !shipsToBuyer ? (
                      <span>
                        <FormattedMessage id="ListingPage.pickupArrange" />
                      </span>
                    ) : null}
                  </span>
                </li>
                <li className={css.fact}>
                  <svg className={css.factIcon} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 12a8 8 0 1 0 2.3-5.7M4 4v4h4" />
                  </svg>
                  <span className={css.factText}>
                    {!returnPolicy.acceptsReturns ? (
                      <>
                        <strong>
                          <FormattedMessage id="ListingPage.noReturnsTitle" />
                        </strong>
                        <span>
                          <FormattedMessage id="ListingPage.returnsNotAccepted" />
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>
                          <FormattedMessage
                            id="ListingPage.returnsTitle"
                            values={{ days: returnPolicy.windowDays }}
                          />
                        </strong>
                        <span>
                          <FormattedMessage
                            id="ListingPage.returnsDetail"
                            values={{
                              shippingPayer: returnPolicy.returnShippingPaidBy,
                              restockingFee: returnPolicy.restockingFeePercent,
                            }}
                          />
                        </span>
                      </>
                    )}
                  </span>
                </li>
                <li className={classNames(css.fact, css.factProtect)}>
                  <svg className={css.factIcon} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <span className={css.factText}>
                    <strong>
                      <FormattedMessage id="ListingPage.tradProtectTitle" />
                    </strong>
                    <span>
                      <FormattedMessage id="ListingPage.tradProtectSubtext" />{' '}
                      <NamedLink name="PurchaseProtectionPage" className={css.factLink}>
                        <FormattedMessage id="ListingPage.tradProtectHow" />
                      </NamedLink>
                    </span>
                  </span>
                </li>
              </ul>
            </div>
            <div className={css.descriptionColumn}>
              <SectionTextMaybe
                heading={intl.formatMessage({ id: 'ListingPage.descriptionHeading' })}
                text={description}
                htmlText={publicData.descriptionHtml}
                showAsIngress
              />
            </div>
          </div>
        </div>

        <OfferModal
          config={config}
          onManageDisableScrolling={onManageDisableScrolling}
          isOffersModalOpen={isOffersModalOpen}
          onOpenOffersModal={setIsOffersModalOpen}
          listingTitle={title}
          listing={currentListing}
          params={params}
          listingId={listingId}
          authorDisplayName={authorDisplayName}
        />
        <SocialSharing
          onManageDisableScrolling={onManageDisableScrolling}
          isSocialSharingModalOpen={isSocialSharingModalOpen}
          onOpenShareModal={setIsSocialSharingModalOpen}
          location={location}
          listing={currentListing}
          description={description}
          listingTitle={title}
          productURL={productURL}
          listingImage={isArrayLength(facebookImages) && facebookImages[0]}
        />
        <Modal
          id="ListingPage.inquiry"
          contentClassName={css.inquiryModalContent}
          isOpen={isAuthenticated && inquiryModalOpen}
          onClose={() => setInquiryModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <InquiryForm
            className={css.inquiryForm}
            submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
            listingTitle={title}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={onSubmitInquiry}
            inProgress={sendInquiryInProgress}
          />
        </Modal>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  listingConfig: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  listingConfig: object,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  listingCategories: array,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const listingCategories = getListingCategories(state);

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    listingCategories,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
